import {
  Container,
  Box,
  Avatar,
  Typography,
  TextField,
  Button,
  Grid,
  Link,
  Alert,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import axios, { AxiosError } from "axios";
import { useMutation, useQuery } from "react-query";
import { API_URL, DOMINIO_ADMIN } from "../consts";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { oraPrenotaStore } from "../oraPrenotaStore";
import { Link as RouterLink } from "react-router-dom";
import { useSnackbar } from "notistack";
import { Clubs } from "../prismaTypes";
import { Loading } from "../ui/Loading";
import { queryClient } from "../App";

interface LoginDto {
  username: string;
  password: string;
}

export function SignIn() {
  const navigate = useNavigate();
  const setToken = oraPrenotaStore((state) => state.setToken);
  const setRole = oraPrenotaStore((state) => state.setRole);
  const setM = oraPrenotaStore((state) => state.setMessaggi);

  const { enqueueSnackbar } = useSnackbar();

  const { error, mutate } = useMutation(
    (login: LoginDto) => {
      return axios.post(API_URL + "auth/login", login);
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries();
        if ("data" in data) {
          const { access_token, ruolo } = data.data;
          if (access_token && ruolo) {
            setToken(access_token);
            setRole(ruolo);
            setM(false);
            if (ruolo === "USER") navigate("/app/tabelloneTariffe/prenota", { replace: true });
            else if (ruolo === "CLUB_ADMIN" || ruolo === "CLUB_BACKOFFICE")
              navigate("/app/tabelloneTariffe/prenota", { replace: true });
            else if (ruolo === "ADMIN") navigate("/app/clubs", { replace: true });
            else navigate("/app", { replace: true });
          }
        } else
          enqueueSnackbar("Credenziali errata!", {
            variant: "error",
          });
      },
      onError: (err: AxiosError) => {
        queryClient.clear();
        if (err.response?.status === 401)
          enqueueSnackbar("Credenziali errata!", {
            variant: "error",
          });
        else
          enqueueSnackbar(error?.response?.data?.message ?? "Si è verificato un errore!", {
            variant: "error",
          });
      },
    }
  );

  const enabled =
    window.location.host.split(".").length > 2 &&
    window.location.host.split(".")[0] !== DOMINIO_ADMIN;
  const { isLoading, data: club } = useQuery<{ club: Clubs }, AxiosError>(
    ["club/getByDomain", window.location.host],
    () => axios.get(API_URL + "club/getByDomain").then((res) => res.data),
    {
      enabled,
      refetchOnWindowFocus: false,
    }
  );

  let errorMsg = "";
  if (error instanceof Error) errorMsg = error.message;

  const [empty, setEmpty] = useState(false);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const data = new FormData(event.currentTarget);

    const username = data.get("email")?.toString();
    const password = data.get("password")?.toString();

    if (username && password) {
      mutate({
        username,
        password,
      });
      setEmpty(false);
    } else setEmpty(true);
  };

  if (isLoading && enabled) return <Loading />;
  if (enabled && club?.club === null)
    return (
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            marginTop: 8,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          Club non trovato!
        </Box>
      </Container>
    );

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {club?.club !== null && club?.club?.logo && (
          <Box sx={{ m: 1 }}>
            <img
              height="180px"
              src={API_URL + "/loghi/" + club?.club?.logo}
              crossOrigin="anonymous"
              alt="logo"
            />
          </Box>
        )}
        {!(club?.club !== null && club?.club?.logo) && (
          <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
            <LockOutlinedIcon />
          </Avatar>
        )}
        <Typography component="h1" variant="h5">
          {(club?.club !== null && club?.club?.nome) ?? "Sign in"}
        </Typography>

        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="email"
            label="Username"
            name="email"
            autoComplete="email"
            autoFocus
          />
          <TextField
            margin="normal"
            required
            fullWidth
            name="password"
            label="Password"
            type="password"
            id="password"
            autoComplete="current-password"
          />

          <Button type="submit" fullWidth variant="contained" sx={{ mt: 3, mb: 2 }}>
            Sign In
          </Button>
          <Grid container>
            <Grid item xs>
              <Link to="/forgotPassword" variant="body2" component={RouterLink}>
                Recupera Password
              </Link>
            </Grid>
          </Grid>
          {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
          {empty && <Alert severity="error">Prego inserire email e password</Alert>}
        </Box>
      </Box>
    </Container>
  );
}
